import { createReducer, emptyProps, on, props } from "@ngrx/store";
import { initialCameraState } from "../camera.state";
import { CameraActions } from "../camera.actions";
import { onLoadableLoad } from "../../../models";
import { arrayToILoadableWrapperArray, createDefaultILoadableArray, createDefaultLoadable, ILoadableArray, ILoadableWrapper, onLoadableError, onLoadableSuccess, toLoadableWrapper } from "../../../models/loadable";
import { removeItem, replaceItem } from "../../common";
import { UserActions } from "../../user/user.actions";
import { IRule } from "../../../models/rule.interface";

export const ruleReducer = createReducer(
    initialCameraState,
    on(CameraActions.loadAllRules, (state) => state),
    on(CameraActions.loadAllRulesSuccess, (state, { rules }) => ({
        ...state,
        rules: rules.reduce((acc, cur) => {
            const existing = acc[cur.camera_id] ?? createDefaultILoadableArray();
            acc[cur.camera_id] = {
                ...onLoadableSuccess({ ...existing }),
                items: [...existing.items, toLoadableWrapper(cur)]
            }
            return acc
        }, {} as { [key: string]: ILoadableArray<ILoadableWrapper<IRule>> })
    })),
    on(CameraActions.loadAllRulesFailure, (state) => state),

    on(CameraActions.loadRules, (state, { sensor }) => ({
        ...state,
        rules: {
            ...state.rules,
            [sensor.id]: {
                ...onLoadableLoad({ ...(state.rules[sensor.id] ?? createDefaultILoadableArray()) })
            }
        }
    })),
    on(CameraActions.loadRulesSuccess, (state, { sensor, rules }) => ({
        ...state,
        rules: {
            ...state.rules,
            [sensor.id]: {
                ...onLoadableSuccess({ ...(state.rules[sensor.id]) }), items: arrayToILoadableWrapperArray(rules)
            }
        }
    })),
    on(CameraActions.loadRulesFailure, (state, { sensor, error }) => ({
        ...state,
        rules: {
            ...state.rules,
            [sensor.id]: {
                ...onLoadableError({ ...(state.rules[sensor.id]) }, error)
            }
        }
    })),
    on(CameraActions.deleteRule, (state, { rule }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rule.camera_id]: {
                ...state.rules[rule.camera_id],
                items: replaceItem(state.rules[rule.camera_id].items, onLoadableLoad(toLoadableWrapper({ ...rule })))
            }
        }
    })),
    on(CameraActions.deleteRuleSuccess, (state, { rule }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rule.camera_id]: {
                ...state.rules[rule.camera_id],
                items: removeItem(state.rules[rule.camera_id].items, rule.id)
            }
        }
    })),
    on(CameraActions.deleteRuleFailure, (state, { rule, error }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rule.camera_id]: {
                ...state.rules[rule.camera_id],
                items: replaceItem(state.rules[rule.camera_id].items, onLoadableError(toLoadableWrapper(state.rules[rule.camera_id].items.filter(e => e.id === rule.id)[0]), error))
            }
        }
    })),
    on(CameraActions.createRules, (state, { rules }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rules[0].camera_id]: onLoadableLoad(toLoadableWrapper(state.rules[rules[0].camera_id]))
        }
    })),
    on(CameraActions.createRulesSuccess, (state, { cameraID }) => ({
        ...state,
        rules: {
            ...state.rules,
            [cameraID]: onLoadableSuccess({ ...state.rules[cameraID] })
        }
    })),
    on(CameraActions.createRulesError, (state, { sensorId, error }) => ({
        ...state,
        rules: {
            ...state.rules,
            [sensorId]: {
                ...state.rules[sensorId],
                ...onLoadableError(toLoadableWrapper(state.rules[sensorId]), error),
                items: [...state.rules[sensorId].items.filter(e => e.id !== undefined)]
            }
        }
    })),

    on(CameraActions.updateRule, (state, { rule }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rule.camera_id]: {
                ...state.rules[rule.camera_id],
                items: replaceItem(state.rules[rule.camera_id].items, onLoadableLoad(toLoadableWrapper({ ...rule })))
            }
        }
    })),
    on(CameraActions.updateRuleSuccess, (state, { rule }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rule.camera_id]: {
                ...state.rules[rule.camera_id],
                items: replaceItem(state.rules[rule.camera_id].items, onLoadableSuccess(toLoadableWrapper({ ...rule })))
            }
        }
    })),
    on(CameraActions.updateRuleError, (state, { rule, error }) => ({
        ...state,
        rules: {
            ...state.rules,
            [rule.camera_id]: {
                ...state.rules[rule.camera_id],
                items: replaceItem(state.rules[rule.camera_id].items, onLoadableError(state.rules[rule.camera_id].items.filter(e => e.id === rule.id)[0], error))
            }
        }
    })),

    on(CameraActions.bulkUpdateRules, (state, { rules }) => {
        const updatedRules = { ...state.rules };
        rules.forEach(rule => {
            if (updatedRules[rule.camera_id]) {
                updatedRules[rule.camera_id] = {
                    ...updatedRules[rule.camera_id],
                    items: replaceItem(updatedRules[rule.camera_id].items, onLoadableLoad(toLoadableWrapper({ ...rule })))
                };
            }
        });
        return { ...state, rules: updatedRules };
    }),
    on(CameraActions.bulkUpdateRulesSuccess, (state, { rules }) => {
        const updatedRules = { ...state.rules };
        rules.forEach(rule => {
            if (updatedRules[rule.camera_id]) {
                updatedRules[rule.camera_id] = {
                    ...updatedRules[rule.camera_id],
                    items: replaceItem(updatedRules[rule.camera_id].items, onLoadableSuccess(toLoadableWrapper({ ...rule })))
                };
            }
        });
        return { ...state, rules: updatedRules };
    }),
    on(CameraActions.bulkUpdateRulesError, (state, { rules, error }) => {
        const updatedRules = { ...state.rules };
        rules.forEach(rule => {
            if (updatedRules[rule.camera_id]) {
                const existing = updatedRules[rule.camera_id].items.filter(e => e.id === rule.id)[0];
                if (existing) {
                    updatedRules[rule.camera_id] = {
                        ...updatedRules[rule.camera_id],
                        items: replaceItem(updatedRules[rule.camera_id].items, onLoadableError(existing, error))
                    };
                }
            }
        });
        return { ...state, rules: updatedRules };
    }),

    on(CameraActions.bulkDeleteRules, (state, { ruleIds }) => {
        const updatedRules = { ...state.rules };
        const ruleIdSet = new Set(ruleIds);
        Object.keys(updatedRules).forEach(cameraId => {
            const bucket = updatedRules[cameraId];
            const hasMatch = bucket.items.some(item => ruleIdSet.has(item.id));
            if (hasMatch) {
                updatedRules[cameraId] = {
                    ...bucket,
                    items: bucket.items.map(item =>
                        ruleIdSet.has(item.id) ? onLoadableLoad(toLoadableWrapper({ ...item })) : item
                    )
                };
            }
        });
        return { ...state, rules: updatedRules };
    }),
    on(CameraActions.bulkDeleteRulesSuccess, (state, { ruleIds }) => {
        const updatedRules = { ...state.rules };
        const ruleIdSet = new Set(ruleIds);
        Object.keys(updatedRules).forEach(cameraId => {
            const bucket = updatedRules[cameraId];
            const filtered = bucket.items.filter(item => !ruleIdSet.has(item.id));
            if (filtered.length !== bucket.items.length) {
                updatedRules[cameraId] = {
                    ...bucket,
                    items: filtered
                };
            }
        });
        return { ...state, rules: updatedRules };
    }),
    on(CameraActions.bulkDeleteRulesError, (state, { ruleIds, error }) => {
        const updatedRules = { ...state.rules };
        const ruleIdSet = new Set(ruleIds);
        Object.keys(updatedRules).forEach(cameraId => {
            const bucket = updatedRules[cameraId];
            const hasMatch = bucket.items.some(item => ruleIdSet.has(item.id));
            if (hasMatch) {
                updatedRules[cameraId] = {
                    ...bucket,
                    items: bucket.items.map(item =>
                        ruleIdSet.has(item.id) ? onLoadableError(item, error) : item
                    )
                };
            }
        });
        return { ...state, rules: updatedRules };
    }),

    on(UserActions.logout, _ => ({ ...initialCameraState })),
);
